import qs from "qs";
import { connectPagination } from "react-instantsearch-dom";

import { useLocation } from "@reach/router";
import { Box, List } from "@vinomofo/components";
import { ArrowLeftIcon, ArrowRightIcon } from "@vinomofo/icons";

import { PageControl } from "./PageControl";

const getPageIndices = (begin: number, end: number) => {
  const indices = [];
  for (let i = begin; i <= end; i += 1) {
    indices.push(i);
  }
  return indices;
};

const getPagePath = (page, queryParams) => {
  let nextParams = queryParams;
  if (page > 1) {
    nextParams = { ...queryParams, page: page.toString() };
  } else {
    delete nextParams["page"];
  }

  const queryString = qs.stringify(nextParams, {
    addQueryPrefix: true,
    arrayFormat: "repeat",
  });

  return `${location.pathname}${queryString}`;
};

export const Pagination = connectPagination(
  ({
    currentRefinement,
    nbPages,
    numberEdgePages = 1,
    numberMiddlePages: numberMiddlePagesProp = 3,
  }): JSX.Element => {
    if (nbPages === 1) return null;

    const location = useLocation();
    const queryParams = qs.parse(location.search.slice(1));

    const beginPages = getPageIndices(1, Math.min(numberEdgePages, nbPages));
    const endPages = getPageIndices(
      Math.max(nbPages - numberEdgePages + 1, numberEdgePages + 1),
      nbPages
    );

    let numberMiddlePages: number;
    if (numberMiddlePagesProp < 1) {
      numberMiddlePages = 1;
      console.warn(
        // eslint-disable-next-line max-len
        `Property "numberMiddlePages" should not be < 1. One middle page button will be shown. Set "numberMiddlePages" >= 1 to remove this warning.`
      );
    } else numberMiddlePages = numberMiddlePagesProp;

    let startingMiddlePages;
    if (numberMiddlePages % 2) {
      // odd
      startingMiddlePages = Math.min(
        currentRefinement - Math.floor(numberMiddlePages / 2),
        nbPages - numberEdgePages - numberMiddlePages
      );
    } else {
      // even, cannot split equally around active page
      // let extra page appear on middlePagesEnd instead
      startingMiddlePages = Math.min(
        currentRefinement - Math.floor(numberMiddlePages / 2) + 1,
        nbPages - numberEdgePages - numberMiddlePages
      );
    }

    const middlePagesBegin = Math.max(startingMiddlePages, numberEdgePages + 2);
    const middlePagesEnd = Math.min(
      Math.max(
        currentRefinement + Math.floor(numberMiddlePages / 2),
        numberEdgePages + numberMiddlePages + 1
      ),
      endPages.length > 0 ? endPages[0] - 2 : nbPages - 1
    );

    const middlePages = getPageIndices(middlePagesBegin, middlePagesEnd);

    let beginFlex = [];
    if (middlePagesBegin > numberEdgePages + 2) {
      beginFlex = ["more-prev"];
    } else if (numberEdgePages + 1 < nbPages - numberEdgePages) {
      beginFlex = [numberEdgePages + 1];
    }

    let endFlex = [];
    if (middlePagesEnd < nbPages - numberEdgePages - 1) {
      endFlex = ["more-next"];
    } else if (nbPages - numberEdgePages > numberEdgePages) {
      endFlex = [nbPages - numberEdgePages];
    }

    const navProps = {
      next: {
        isDisabled: currentRefinement === nbPages,
        to: getPagePath(currentRefinement + 1, queryParams),
        icon: <ArrowRightIcon />,
      },
      previous: {
        isDisabled: currentRefinement === 1,
        to: getPagePath(currentRefinement - 1, queryParams),
        icon: <ArrowLeftIcon />,
      },
    };

    let controls = [
      "previous",
      ...beginPages,
      ...beginFlex,
      ...middlePages,
      ...endFlex,
      ...endPages,
      "next",
    ];

    controls = controls.map((control) => ({
      isActive: control === currentRefinement,
      control,
      to: getPagePath(control, queryParams),
      separator: control === "more-prev" || control === "more-next",
      ...navProps[control],
    }));

    return (
      <Box
        display="flex"
        flexBasis="100%"
        justifyContent="center"
        paddingTop="30px"
      >
        <List listStyleType="none" display="flex" alignItems="center">
          {controls.map((control, index) => {
            return <PageControl key={index} {...control} />;
          })}
        </List>
      </Box>
    );
  }
);
